/**
 * THE PASSPORT WORK LENS — a way of LOOKING at one person's record, and
 * emphatically not a fourth kind of role.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * THREE CONCEPTS THAT SHARE SOME SPELLINGS AND NOTHING ELSE
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   1. ACCESS / AUTHORITY        `memberships.authority_role`
 *      OWNER, MANAGER, STAFF…    "what may this person administer?"
 *
 *   2. CURRENT SHOP WORK ROLES   `membership_work_roles`
 *      TECHNICIAN,               "what work may this person perform NOW, for
 *      SERVICE_ADVISOR,           THIS shop?"
 *      DETAIL_SPECIALIST…
 *
 *   3. PASSPORT IDENTITY         `passport_profiles.roles[]`
 *      owner, technician,        the person's own portable professional
 *      student, instructor…      identity and discoverability vocabulary.
 *                                It belongs to THEM, not to an employer.
 *
 * Some labels overlap. Nothing may be copied between them because of that.
 * This module deliberately CANNOT see concept 1 and CANNOT see concept 3: its
 * input type admits neither an `AuthorityRole` nor a Passport role string, so
 * "OWNER authority produced an Owner work lens" is unwritable here rather than
 * merely discouraged. Widening `passport_profiles.roles` to carry a shop work
 * role — or syncing `membership_work_roles` into it — is forbidden, and this
 * module exists precisely so that neither is ever needed.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHAT A LENS IS
 * ═══════════════════════════════════════════════════════════════════════════
 * A lens answers one presentation question:
 *
 *   "Show me this person's Passport evidence for the work they performed in
 *    this capacity."
 *
 * Switching a lens changes what is DRAWN. It writes nothing. No membership, no
 * work role, no authority, no Passport profile, no technician profile and no
 * historical allocation is touched by selecting one, and there is no server
 * call at all.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY ELIGIBILITY IS A UNION, AND WHY THE HISTORICAL HALF IS A SNAPSHOT
 * ═══════════════════════════════════════════════════════════════════════════
 * Andy holds OWNER + TECHNICIAN + DETAIL_SPECIALIST. He details on Saturdays.
 * In March somebody removes DETAIL_SPECIALIST.
 *
 * If available lenses were "today's work roles", the Detail lens would vanish
 * and every Saturday he ever worked would become unreachable — the record
 * would have been edited by an act that was about NEXT week. So availability
 * is the union of:
 *
 *   A. CURRENT    — the canonical `membership_work_roles` set, and
 *   B. HISTORICAL — the distinct `performed_work_role` values RECORDED on the
 *                   evidence itself (`ticket_job_time_allocations`, surfaced
 *                   by `ticket_job_performer_evidence`).
 *
 * (B) is captured AT ALLOCATION by `__capture_performed_work_role` and never
 * recomputed — migration 20261209180000. That is what makes it usable here:
 * it is a fact about the day the minutes were earned. Deriving last year's
 * work by joining this year's roles would produce a page that CHANGES when
 * somebody is promoted, which is the defect this whole model exists to avoid.
 *
 * A role that is both is marked both; the UI says "Current role" or
 * "Historical role" from these two booleans and never guesses.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY ONLY THREE
 * ═══════════════════════════════════════════════════════════════════════════
 * TECHNICIAN, SERVICE_ADVISOR and DETAIL_SPECIALIST are the operational
 * capacities the product measures work in. DISPATCHER and PARTS are real work
 * roles that the evidence layer records nothing for, so a lens for them would
 * open onto an empty page and imply the measurement exists. They are reported
 * separately as `nonLensWorkRoles` — visible as held, never drawn as a lens —
 * rather than silently dropped.
 *
 * OWNER and MANAGER are authority, not work. Leadership Passport intelligence
 * has its own surface (`ManagerPassport`); see `PASSPORT_SECTION_EVIDENCE`.
 */

import { WORK_ROLE_LABELS, type WorkRole } from '@/lib/roles/multiRole';

// ───────────────────────────────────────────────────────────────────────────
// Vocabulary
// ───────────────────────────────────────────────────────────────────────────

/** The operational capacities a Passport can be read through. */
export type PassportWorkLens = 'TECHNICIAN' | 'SERVICE_ADVISOR' | 'DETAIL_SPECIALIST';

/** Stable display order. Also the tie-break order for a default selection. */
export const PASSPORT_WORK_LENSES: readonly PassportWorkLens[] = [
  'TECHNICIAN',
  'SERVICE_ADVISOR',
  'DETAIL_SPECIALIST',
] as const;

const LENS_SET: ReadonlySet<string> = new Set(PASSPORT_WORK_LENSES);

/** A work role this build knows and deliberately does not draw as a lens. */
export const NON_LENS_WORK_ROLES: readonly WorkRole[] = ['DISPATCHER', 'PARTS'] as const;

export const isPassportWorkLens = (v: unknown): v is PassportWorkLens =>
  typeof v === 'string' && LENS_SET.has(v);

/** The label a human sees. Shared with the role model — one spelling, one place. */
export const workLensLabel = (lens: PassportWorkLens): string => WORK_ROLE_LABELS[lens];

// ───────────────────────────────────────────────────────────────────────────
// Availability
// ───────────────────────────────────────────────────────────────────────────

export interface PassportWorkLensAvailability {
  readonly role: PassportWorkLens;
  readonly label: string;
  /** Held in `membership_work_roles` today. */
  readonly current: boolean;
  /** Recorded on at least one historical allocation as the performed role. */
  readonly historical: boolean;
}

/**
 * Provenance in one word, for help copy. A role that is both is reported as
 * `current`, because that is what the person is: currently doing the work,
 * and with history behind them.
 */
export type WorkLensProvenance = 'current' | 'historical';

export const lensProvenance = (a: PassportWorkLensAvailability): WorkLensProvenance =>
  a.current ? 'current' : 'historical';

export const PROVENANCE_LABEL: Readonly<Record<WorkLensProvenance, string>> = {
  current: 'Current role',
  historical: 'Historical role',
};

export interface PassportWorkLensInput {
  /**
   * The canonical current work-role set.
   *
   * `null`/`undefined` means NOT LOADED and makes the whole resolution
   * `resolving`. An EMPTY ARRAY means somebody stated this person performs no
   * operational work, and is honoured — see `currentWorkRolesStated`.
   */
  readonly currentWorkRoles?: readonly (WorkRole | string)[] | null;
  /**
   * Whether anybody has actually answered the work-role question. Mirrors
   * `ResolvedMembershipRoles.workRolesStated`.
   *
   * An UNSTATED empty set is not a statement that the person does nothing, so
   * it produces no current lenses either — but it also must not be presented
   * as "somebody decided they do nothing". Both cases yield zero current
   * lenses; the flag travels so the empty state can be worded honestly.
   */
  readonly currentWorkRolesStated?: boolean;
  /**
   * Distinct `performed_work_role` values found on this person's historical
   * evidence. `null`/`undefined` means NOT LOADED.
   *
   * Values this build does not recognise are ignored rather than rendered — a
   * role name from a future migration must not appear as a lens that nothing
   * knows how to draw.
   */
  readonly historicalPerformedRoles?: readonly (string | null | undefined)[] | null;
  /**
   * True when historical evidence exists whose performed role was never
   * recorded (rows written before multi-role, or work whose family nobody in
   * the shop claimed). NULL means "not recorded", never "none" — so it is
   * surfaced rather than counted as absence.
   */
  readonly hasUnattributedHistory?: boolean;
}

export interface PassportWorkLensResolution {
  /** Every lens this person may be read through, in `PASSPORT_WORK_LENSES` order. */
  readonly lenses: readonly PassportWorkLensAvailability[];
  /**
   * True while either half is still unloaded. A switcher MUST NOT be drawn —
   * showing "Technician" alone and then adding "Detail Specialist" a moment
   * later reads as the person having just been given a role.
   */
  readonly resolving: boolean;
  /** Held work roles that this build deliberately does not draw as a lens. */
  readonly nonLensWorkRoles: readonly WorkRole[];
  /** Somebody has stated the current work-role set (even if it is empty). */
  readonly currentWorkRolesStated: boolean;
  /** Evidence exists whose performed role was never recorded. */
  readonly hasUnattributedHistory: boolean;
}

/**
 * THE canonical resolver. One implementation, so two Passport surfaces cannot
 * disagree about which lenses a person has.
 */
export function resolvePassportWorkLenses(
  input: PassportWorkLensInput,
): PassportWorkLensResolution {
  const currentLoaded = input.currentWorkRoles != null;
  const historyLoaded = input.historicalPerformedRoles != null;

  const current = new Set<PassportWorkLens>();
  const heldNonLens = new Set<string>();
  if (currentLoaded) {
    for (const r of input.currentWorkRoles!) {
      if (isPassportWorkLens(r)) current.add(r);
      else if ((NON_LENS_WORK_ROLES as readonly string[]).includes(String(r))) {
        heldNonLens.add(String(r));
      }
    }
  }

  const historical = new Set<PassportWorkLens>();
  if (historyLoaded) {
    for (const r of input.historicalPerformedRoles!) {
      if (isPassportWorkLens(r)) historical.add(r);
    }
  }

  const lenses = PASSPORT_WORK_LENSES
    .filter((r) => current.has(r) || historical.has(r))
    .map<PassportWorkLensAvailability>((role) => ({
      role,
      label: workLensLabel(role),
      current: current.has(role),
      historical: historical.has(role),
    }));

  return {
    lenses,
    resolving: !currentLoaded || !historyLoaded,
    nonLensWorkRoles: NON_LENS_WORK_ROLES.filter((r) => heldNonLens.has(r)),
    currentWorkRolesStated: !!input.currentWorkRolesStated,
    hasUnattributedHistory: !!input.hasUnattributedHistory,
  };
}

/**
 * Which lens should be SELECTED, given what is available and what the viewer
 * last asked for?
 *
 * A requested lens that is not available is not honoured — a link, or a piece
 * of stale component state, must not open a Detail lens on somebody who has
 * never detailed. Otherwise the first CURRENT lens wins, because a person's
 * present job is the sensible thing to open on; only if they hold none does a
 * historical lens get selected.
 *
 * Returns `null` when there is nothing to select, which is a real state: a
 * person with no operational work roles and no recorded work has no work lens,
 * and their Passport is identity and application evidence.
 */
export function selectPassportWorkLens(
  resolution: PassportWorkLensResolution,
  requested?: PassportWorkLens | string | null,
): PassportWorkLens | null {
  const available = resolution.lenses;
  if (available.length === 0) return null;

  if (isPassportWorkLens(requested)) {
    const hit = available.find((l) => l.role === requested);
    if (hit) return hit.role;
  }

  return (available.find((l) => l.current) ?? available[0]).role;
}
